<template>
  <div :class="className">
    <label :for="id">
      {{ label }}
      <BaseIcon class="base-input__padlock" v-if="isLocked" :name="'locked-padlock'"/>
    </label>
    <div class="base-input__wrapper">
      <p class="wrapper__prefix" v-if="prefix">{{ prefix }}</p>
      <input v-bind="$attrs" :aria-describedby="describedBy" :aria-errormessage="errorMessage" :id="id" :disabled="isFieldDisabled" :value="value | formatVal(formatNum)" v-on="listeners" v-validate="validate">
      <p class="wrapper__suffix" v-if="suffix">{{ suffix }}</p>
      <div class="wrapper__arrows" v-if="$attrs.type === 'number'">
        <button @click="increaseVal">
          <BaseIcon name="arrow"></BaseIcon>
        </button>
        <button @click="decreaseVal">
          <BaseIcon name="arrow"></BaseIcon>
        </button>
      </div>
    </div>
    <TypographyInputError :id="errorMessageId" v-if="hasErrors">{{ errors.first($attrs.name) }}</TypographyInputError>
    <TypographyInputHint :id="hintId" v-if="hint" >{{ hint }}</TypographyInputHint>
    <TypographyMeta v-if="meta" v-html="meta"></TypographyMeta>
  </div>
</template>

<script>
  export default {
    inject: ['$validator'],
    props: {
      theme: String, // disabled, grayed
      label: String,
      value: { default: '' },
      hint: String,
      meta: String,
      validate: {
        type: [String, Object],
        default: ''
      },
      formatNum: String,
      minVal: [ String, Number, Boolean ],
      maxVal: [ String, Number, Boolean ],
      prefix: String,
      suffix: String,
      isLocked: Boolean
    },
    data () {
      return {
        id: null,
        focus: false
      }
    },
    mounted () {
      this.id = this._uid
    },
    computed: {
      hintId () {
        return this._uid + "-hint"
      },
      errorMessageId () {
        return this._uid + "-error"
      },
      describedBy () {
        if (this.hint) {
          return this.hintId
        }

        return ""
      },
      errorMessage () {
        if (this.hasErrors) {
          return this.errorMessageId
        }

        return ""
      },
      hasErrors () {
        return this.errors.has(this.$attrs.name)
      },
      className () {
        return [
          'base-input',
          {
            'base-input--error': this.hasErrors,
            'base-input--focus': this.focus,
            [`base-input--${this.theme}`]: this.theme,
            'base-input--locked': this.isLocked,
          }
        ]
      },
      listeners () {
        return {
          ...this.$listeners,
          input: e => this.$emit('input', e.target.value),
          blur: () => this.onFocus(false),
          focus: () => this.onFocus(true)
        }
      },
      isFieldDisabled () {
        return this.theme === 'disabled' || this.isLocked
      }
    },
    filters: {
      formatVal(val, format) {
        if(!format) return val

        const prefix = '0'.repeat(format.length - 1)

        if(val <= -10 || val >= 10) {
          return val
        } else if (val < 0) {
          return '-' + prefix + Math.abs(val)
        } else if (val >= 0) {
          return prefix + val
        }
      }
    },
    methods: {
      min () {
        const v = this.minVal
        if (v === false || v === true || v === undefined || v === null) {
          return -Infinity
        } else {
          return +v
        }
      },
      max () {
        const v = this.maxVal
        if (v === false || v === true || v === undefined || v === null) {
          return Infinity
        } else {
          return +v
        }
      },
      increaseVal () {
        const incVal = +this.value + 1
        const emitVal = incVal > this.max() ? this.max() : incVal < this.min() ? this.min() : incVal
        this.$emit('input', emitVal)
      },
      decreaseVal () {
        const deVal = +this.value - 1
        const emitVal = deVal < this.min() ? this.min() : deVal > this.max() ? this.max() : deVal
        this.$emit('input', emitVal)
      },
      onFocus (val) {
        this.focus = val
      },
    }
  }
</script>

<style lang="scss">
  @import './src/assets/styles/colors.scss';
  @import './src/assets/styles/fonts.scss';
  @import './src/assets/styles/mixins.scss';

  .base-input {
    position: relative;
    margin-bottom: 20px;
    font-size: 14.6px;
    line-height: 1.43;

    &__wrapper {
      position: relative;
      display: flex;
      align-items: center;

      input {
        width: 100%;
      }
    }

    .wrapper__prefix {
      padding-right: 13px;
      color: $color-pale-sky;
    }

    .wrapper__suffix {
      padding-right: 19px;
      padding-left: 13px;
      margin-left: auto;
      color: $color-pale-sky;
    }

    .wrapper__arrows {
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      width: 20px;
      border-left: 1px solid $color-border-form-field;

      button {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 50%;
        background: linear-gradient(180deg, #fff0, #d6d6d624);

        &:first-of-type {
          border-bottom: 1px solid $color-border-form-field;

          svg {
            transform: rotateZ(180deg);
          }
        }

        svg {
          width: 6px;
          height: 3px;
          color: #656C74;
        }
      }
    }

    label {
      display: block;
      margin-bottom: 4px;
    }

    textarea {
      width: 100% !important;
    }

    &__wrapper, textarea, select {
      width: 100%;
      height: 38px;
      padding: 8px 13px;
      border: 1px solid $color-border-form-field;
      border-radius: 3px;
      transition-property: box-shadow, border-color;
      transition-duration: .2s;
      transition-timing-function: cubic-bezier(.64, 0, .35, 1);
      box-shadow: inset 0 1px 0 0 rgba(99, 115, 129, .05);

      &::placeholder {
        color: #919eab;
      }
    }

    &__learn {
      position: absolute;
      top: 0;
      right: 0;
      color: $color-lochmara;
      cursor: pointer;
    }

    &__select-wrap {
      position: relative;
      width: 100%;
      height: 38px;

      select {
        width: 100%;
        height: 100%;
        padding: 8px 33px 8px 13px;
        background-color: #FBFBFC;
        font-size: inherit;
        appearance: none;
        box-shadow: 0 0 0 1px transparent, 0 1px 0 0 rgba(22,29,37,.05);
      }

      svg {
        position: absolute;
        top: 50%;
        right: 12px;
        width: 8px;
        height: 12px;
        color: $color-pale-sky;
        pointer-events: none;
        transform: translateY(-50%);
      }
    }

    &--disabled {

      .base-input__wrapper {
        background-color: #f9fafb;
        color: #919eab;
        cursor: auto;
        box-shadow: none;
      }
    }

    &--grayed {
      .base-input__wrapper {
        background-color: #f9fafb;
        color: #919eab;
        cursor: auto;
        box-shadow: none;
      }
    }

    &--error {

      .base-input__wrapper, select, textarea {
        background-color: #fbeae5;
        border-color: $color-text-error;
        box-shadow: none;
      }
    }

    &--focus {
      .base-input__wrapper, textarea, select {
        border-color: #5c6ac4;
        box-shadow: inset 0 0 0 0 transparent, 0 0 0 1px #5c6ac4;
      }
    }
    & {
      .base-input__wrapper:focus-within,
      .html-editor textarea:focus {
        border-color: #5c6ac4;
        box-shadow: inset 0 0 0 0 transparent, 0 0 0 1px #5c6ac4;
      }
    }
    &--locked {
      .base-input__wrapper, textarea {
        cursor: pointer;
        background-color: #FBFBFC;
        box-shadow: 0 0 0 1px transparent, 0 1px 0 0 rgba(22,29,37,.05);
      }
      .base-input__padlock {
        width: 12px;
        height: 12px;
      }
      input {
        pointer-events: none;
        color: grayText;
      }
      textarea {
        color: grayText;
      }
    }
  }
</style>


// WEBPACK FOOTER //
// src/components/base/BaseInput.vue